import React from "react";
import { FaPalette, FaTimes } from "react-icons/fa";

// Sample images for modal cards
import createOptionCreateGif from "../../assets/creationoption-thumbnails/create.gif";
import createOptionChangeposeGif from "../../assets/creationoption-thumbnails/change_pose.gif";
import createOptionChangeStyleGif from "../../assets/creationoption-thumbnails/change_style.gif";
import createOptionChangeBackgroundGif from "../../assets/creationoption-thumbnails/change_background.gif";
import createOptionRemoveBackgroundGif from "../../assets/creationoption-thumbnails/remove_background.gif";
import createTextToImageGif from "../../assets/creationoption-thumbnails/text_to_image.gif";

const common_placeholder_text = "Describe in as much details what would you like to see in the final image / video";

const CREATION_OPTIONS = [
  {
    id: 'create_image',
    label: 'Create Product Image',
    desc: 'Add a new person or create an ecommerce image of your product.',
    text_prompt_placeholder: common_placeholder_text,
    img: createOptionCreateGif,
    showReferenceImage: true,
    allowedModels: [
      'black-forest-labs/flux-kontext',
    ],
    allowedAspectRatios: ['1:1', '4:3', '3:4', '16:9', '4:5', '5:4', '9:16', '3:2', '2:3', '21:9'],
    showOutputSamples: false,
    showImageResolution: true,
    overrideInstagramPostType: 'story',
  },
  {
    id: 'text_to_image',
    label: 'Create an Image from Text',
    desc: 'Create sample images from text.',
    text_prompt_placeholder: common_placeholder_text,
    img: createTextToImageGif,
    showReferenceImage: false,
    allowedModels: null,
    allowedAspectRatios: ['1:1', '4:3', '3:4', '16:9', '4:5', '5:4', '9:16', '3:2', '2:3', '21:9'],
    showOutputSamples: false,
    showImageResolution: true,
    overrideInstagramPostType: 'story',
  },
  {
    id: 'change_pose',
    label: 'Change pose of model',
    desc: 'Change subject pose or create variations while keeping identity consistent.',
    text_prompt_placeholder: common_placeholder_text,
    img: createOptionChangeposeGif,
    showReferenceImage: true,
    allowedModels: [
      'black-forest-labs/flux-kontext',
    ],
    allowedAspectRatios: ['1:1', '4:3', '3:4', '16:9', '4:5', '5:4', '9:16', '3:2', '2:3', '21:9'],
    showOutputSamples: false,
    showImageResolution: true,
    overrideInstagramPostType: 'story',
  },
  {
    id: 'change_style',
    label: 'Change Image Style',
    desc: 'Reimagine your image in different styles like retro, cinematic, or illustrative.',
    text_prompt_placeholder: common_placeholder_text,
    img: createOptionChangeStyleGif,
    showReferenceImage: true,
    allowedModels: [
      'logerzhu/ad-inpaint',
      'openai/gpt-image-1'
    ],
    allowedAspectRatios: ['1:1', '4:3', '3:4', '16:9', '4:5', '5:4', '9:16', '3:2', '2:3', '21:9'],
    showOutputSamples: true,
    showImageResolution: true,
    overrideInstagramPostType: 'story',
  },
  // {
  //   id: 'change_background',
  //   label: 'Change background',
  //   desc: 'Swap the scene/background while keeping the foreground object intact.',
  //   text_prompt_placeholder: common_placeholder_text,
  //   img: createOptionChangeBackgroundGif,
  //   showReferenceImage: true,
  //   allowedModels: [
  //     'logerzhu/ad-inpaint',
  //     'black-forest-labs/flux-pro-1.0-fill',
  //   ],
  //   allowedAspectRatios: ['Landscape', 'Portrait', 'Square'],
  //   showOutputSamples: false,
  //   showImageResolution: false,
  //   overrideInstagramPostType: 'story',
  // },
  {
    id: 'remove_background',
    label: 'Remove background',
    desc: 'Precisely remove the background to export clean cutouts for design use.',
    text_prompt_placeholder: 'Text input not available. Please upload the image and click Generate.',
    img: createOptionRemoveBackgroundGif,
    showReferenceImage: true,
    allowedModels: [
      '851-labs/background-remover'
    ],
    allowedAspectRatios: ['1:1', '4:3', '3:4', '16:9', '4:5', '5:4', '9:16', '3:2', '2:3', '21:9'],
    showOutputSamples: false,
    showImageResolution: false,
    overrideInstagramPostType: 'story',
  },
];

const CreationOptionsGrid = ({ 
  options = CREATION_OPTIONS, 
  selectedLabel, 
  onSelect, 
  variant = 'inline', // 'inline' or 'modal'
  showTitle = true,
  className = ""
}) => {
  const isModal = variant === 'modal';
  
  const gridClasses = isModal 
    ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
    : "grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-3 xl:grid-cols-3 gap-2 sm:gap-3";
  
  const containerClasses = isModal
    ? "glass-effect rounded-3xl border border-white/20 bg-[#0a0a0a]/95 backdrop-blur-3xl p-6 sm:p-8 w-[90vw] max-w-6xl max-h-[85vh] overflow-y-auto shadow-2xl flex flex-col"
    : `w-full ${className}`;

  return (
    <div className={containerClasses}>
      {showTitle && (
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-2">Choose Action</h2>
          <p className="text-gray-400 text-sm sm:text-base">Choose from different AI actions like add a model, Change pose, Remove background etc.</p>
        </div>
      )}

      <div className={gridClasses}>
        {options.map(opt => (
          <button
            key={opt.id || opt.label}
            type="button"
            onClick={() => onSelect(opt.label)}
            className={`group relative overflow-hidden rounded-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl border border-white/10 hover:border-white/20 ${
              selectedLabel === opt.label
                ? 'ring-1 ring-pink-400 ring-offset-1 ring-offset-[#0a0a0a] shadow-lg shadow-pink-400/25 border-pink-400/50'
                : 'hover:ring-1 hover:ring-white/30'
            }`}
            title={opt.label}
          >
            <div className="aspect-square relative">
              {opt.img ? (
                <img src={opt.img} alt={opt.label} className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-white/5 to-white/0" />
              )}

              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

              <div className="absolute bottom-1 left-1 right-1 text-center">
                <div className="text-white font-semibold text-[10px] sm:text-xs leading-tight drop-shadow-lg">
                  {opt.label}
                </div>
              </div>

              {selectedLabel === opt.label && (
                <div className="absolute top-1 right-1 w-6 h-6 bg-pink-400 rounded-full flex items-center justify-center">
                  <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                </div>
              )}

              <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-200" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// Modal wrapper component
const CreationOptionsModal = ({ isOpen, onClose, options, selectedLabel, onSelect }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-50">
        <CreationOptionsGrid
          options={options}
          selectedLabel={selectedLabel}
          onSelect={(label) => {
            onSelect(label);
            onClose();
          }}
          variant="modal"
          showTitle={true}
        />
        <button
          type="button"
          className="absolute top-4 right-4 p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-200 z-10"
          title="Close"
          onClick={onClose}
        >
          <FaTimes className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default CreationOptionsGrid;
export { CREATION_OPTIONS, CreationOptionsModal };
